import React, { FC, useState, useEffect } from 'react';
import {
  Card,
  CircularProgress,
  SvgIcon,
  Avatar,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  Collapse,
  Box,
  Paper,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import { useTranslate } from 'react-admin';
import { Title, AmountField, TextField } from '~/components';
import RaTabbedShowLayout from '~/theme/overrides/RaTabbedShowLayout';
import { Alert } from '@material-ui/lab';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import moment from 'moment';
import { useTenantCurrencies } from '~/hooks/tenant';
import { usePermissions } from 'react-admin';
import { NoPermissions } from '~/components/ra';
import { useFetchLiquidityPositions } from '~/hooks/bankAccounts';

const useStyles = makeStyles({
  currencyCell: {
    textAlign: 'left',
    paddingLeft: 8,
  },
  iconCell: {
    width: 30,
    paddingLeft: 8,
    paddingRight: 8,
  },
});

interface BankAccount {
  id: string;
  number: string;
  currency: string;
  status: string;
  alias: string;
  description: string;
  balance: {
    currency: string;
    value: number;
  };
  created: string;
  deleted?: boolean;
}

export const l = (key: string): string => `resources.liquidityBuckets.${key}`;

const groupByCurrency = (accounts: BankAccount[]) => {
  const grouped: Record<string, BankAccount[]> = {};
  accounts.forEach((acc) => {
    if (!grouped[acc.currency]) grouped[acc.currency] = [];
    grouped[acc.currency].push(acc);
  });
  return grouped;
};

interface CollapsibleCurrencyRowProps {
  currency: string;
  accounts: BankAccount[];
}

const CollapsibleCurrencyRow: FC<CollapsibleCurrencyRowProps> = ({ currency, accounts }) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();
  return (
    <>
      <TableRow>
        <TableCell className={classes.iconCell}>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell colSpan={5} className={classes.currencyCell}>
          <b>{currency}</b> ({accounts.length})
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={1}>
              <Table size='small'>
                <TableBody>
                  {accounts.map((acc) => (
                    <TableRow key={acc.id}>
                      <TableCell style={{ width: '30%' }}>
                        <TextField textWrap showLink={`/bank/accounts/${acc.id}/show`}>
                          {acc.alias}
                        </TextField>
                      </TableCell>
                      <TableCell style={{ width: '20%' }}>
                        <AmountField amount={acc.balance} />
                      </TableCell>
                      <TableCell style={{ width: '50%' }}>{acc.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const VirtualLiquidityPositionList: FC<any> = (props) => {
  const translate = useTranslate();
  const t = (key: string, options?: any): string => translate(l(key), options);
  const { permissions } = usePermissions();
  const hasPermissions = permissions?.includes('virtualLiquidityPosition.view');

  const [responseTime, setResponseTime] = useState<string | null>(null);

  useEffect(() => {
    setResponseTime(moment().format('LL LT'));
  }, []);

  const { data, isLoading } = useFetchLiquidityPositions(hasPermissions);

  const { data: tenantCurrencies } = useTenantCurrencies();

  if (!permissions) return null;
  if (!hasPermissions) return <NoPermissions />;
  if (isLoading) {
    return (
      <Box p={3} display='flex' justifyContent='center'>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) return null;

  const grouped = groupByCurrency(data.data);
  const masterCurrency = tenantCurrencies?.find((c) => c.isMaster)?.code;
  const groupedEntries = Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  const sortedEntries = masterCurrency
    ? [
        ...groupedEntries.filter(([currency]) => currency === masterCurrency),
        ...groupedEntries.filter(([currency]) => currency !== masterCurrency),
      ]
    : groupedEntries;
  return (
    <>
      <Title
        resource={props.resource}
        headline={t('title')}
        subtitle={isLoading ? t('loading') : t('subtitle', { responseTime })}
      />
      <Card>
        <div style={RaTabbedShowLayout.content}>
          <Grid container spacing={2} direction='column'>
            <Grid container item spacing={2}>
              <Grid item>
                <Alert severity='warning'>{t('alertText')}</Alert>
              </Grid>
            </Grid>
            <Grid item>
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    {sortedEntries.map(([currency, accounts]) => (
                      <CollapsibleCurrencyRow
                        key={currency}
                        currency={currency}
                        accounts={accounts}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </div>
      </Card>
    </>
  );
};

export const LiquidityBucketsIcon: FC<any> = () => {
  return (
    <Avatar>
      <SvgIcon>
        <BubbleChartIcon />
      </SvgIcon>
    </Avatar>
  );
};

export default VirtualLiquidityPositionList;
